import { Injectable, inject } from '@angular/core';
import { CatalogLabelService } from '@shared/services/i18n/catalog-label.service';
import { SidebarDTO, SidebarGroupDTO, SidebarCategoryDTO, SidebarModuleDTO } from '@models/sidebar.model';

/** Coincidencia plana del buscador de navegación: módulo hoja + su contexto (categoría/grupo). */
export interface SidebarSearchResult {
  group: SidebarGroupDTO;
  category: SidebarCategoryDTO;
  module: SidebarModuleDTO;
}

/** Segmento de texto para resaltar la porción que coincide con la búsqueda. */
export interface HighlightPart {
  text: string;
  match: boolean;
}

/**
 * Lógica de búsqueda del catálogo de navegación, compartida por el menú lateral
 * ({@link AppSidebarMenuComponent}) y la barra superior ({@link AppTopNavComponent}) para no
 * duplicarla. Opera sobre el mismo árbol {@link SidebarDTO} (ya filtrado por permiso+plan en
 * backend) y resuelve las etiquetas con {@link CatalogLabelService}, por lo que la búsqueda
 * respeta el idioma activo. Es tolerante a diacríticos (que "auditoria" encuentre "Auditoría").
 */
@Injectable({ providedIn: 'root' })
export class SidebarSearchService {
  private readonly catalogLabel = inject(CatalogLabelService);

  /**
   * Normaliza para comparar/resaltar: sin acentos y en minúsculas, para que
   * "auditoria" encuentre "Auditoría" (clave en es).
   */
  normalize(text: string): string {
    return text.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
  }

  /**
   * Devuelve los módulos cuyo nombre —o el de su categoría/grupo— contiene la consulta.
   * Los que empiezan por la consulta se ordenan primero (coincidencia más relevante arriba).
   */
  search(data: SidebarDTO | null, rawQuery: string): SidebarSearchResult[] {
    const q = this.normalize(rawQuery.trim());
    if (!q || !data) return [];

    const starts: SidebarSearchResult[] = [];
    const contains: SidebarSearchResult[] = [];

    for (const group of data.groups) {
      const groupMatch = this.normalize(this.catalogLabel.group(group.name)).includes(q);
      for (const category of group.categories) {
        const categoryMatch = this.normalize(
          this.catalogLabel.category(category.name, category.url)
        ).includes(q);
        for (const module of category.modules) {
          const moduleNorm = this.normalize(this.catalogLabel.module(module.name, module.url));
          if (moduleNorm.includes(q) || categoryMatch || groupMatch) {
            const result: SidebarSearchResult = { group, category, module };
            if (moduleNorm.startsWith(q)) starts.push(result);
            else contains.push(result);
          }
        }
      }
    }

    return [...starts, ...contains];
  }

  /**
   * Parte una etiqueta en segmentos {texto, match} para resaltar la porción que coincide con la
   * consulta. Compara sobre el texto normalizado pero mapea de vuelta a los índices del texto
   * ORIGINAL, para resaltar con acentos intactos.
   */
  highlightParts(text: string, rawQuery: string): HighlightPart[] {
    const q = this.normalize(rawQuery.trim());
    if (!q) return [{ text, match: false }];

    // Construye el texto normalizado y un mapa índice-normalizado → índice-original,
    // porque quitar diacríticos puede cambiar la longitud (marcas combinantes).
    const normChars: string[] = [];
    const originalIndex: number[] = [];
    for (let i = 0; i < text.length; i++) {
      const norm = this.normalize(text[i]);
      for (let j = 0; j < norm.length; j++) {
        normChars.push(norm[j]);
        originalIndex.push(i);
      }
    }

    const idx = normChars.join('').indexOf(q);
    if (idx === -1) return [{ text, match: false }];

    const start = originalIndex[idx];
    const endNorm = idx + q.length;
    const end = endNorm < originalIndex.length ? originalIndex[endNorm] : text.length;

    const parts: HighlightPart[] = [];
    if (start > 0) parts.push({ text: text.slice(0, start), match: false });
    parts.push({ text: text.slice(start, end), match: true });
    if (end < text.length) parts.push({ text: text.slice(end), match: false });
    return parts;
  }
}
